import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import AccountDropdown from './AccountDropdown';
import ChatPanel from './ChatPanel';
import RecommendedInitiatives  from './intelligence/RecommendedInitiatives';
import CostIntelligence        from './intelligence/CostIntelligence';
import MemberIntelligence      from './intelligence/MemberIntelligence';
import ProviderIntelligence    from './intelligence/ProviderIntelligence';
import PredictiveIntelligence  from './intelligence/PredictiveIntelligence';
import PerformanceIntelligence from './intelligence/PerformanceIntelligence';
import { FilterProvider } from './intelligence/FilterShelf';
import './DashboardPage.css';

// ── Tabs config ───────────────────────────────────────────────────────────────

const TABS = [
  { key: 'recommended', label: 'Recommended Initiatives', Component: RecommendedInitiatives  },
  { key: 'cost',        label: 'Cost Intelligence',        Component: CostIntelligence        },
  { key: 'member',      label: 'Member Intelligence',      Component: MemberIntelligence      },
  { key: 'provider',    label: 'Provider Intelligence',    Component: ProviderIntelligence    },
  { key: 'predictive',  label: 'Predictive Intelligence',  Component: PredictiveIntelligence  },
  { key: 'performance', label: 'Performance Intelligence', Component: PerformanceIntelligence },
];

function TabContent({ activeTab }) {
  const { Component } = TABS.find(t => t.key === activeTab);
  return (
    <div className="dash-tabs-pane h-100 d-flex flex-column">
      <div className="flex-grow-1 overflow-auto" style={{ overscrollBehaviorY: 'contain' }}>
        <Component />
      </div>
    </div>
  );
}

// ── Dashboard page ────────────────────────────────────────────────────────────

const VALID_TAB_KEYS = TABS.map(t => t.key);
function tabFromURL() {
  const p = new URLSearchParams(window.location.search).get('tab');
  return VALID_TAB_KEYS.includes(p) ? p : 'recommended';
}

export default function DashboardPage({ onNavigate, currentUser, onLogout, chatStateRef }) {
  const leftPanelRef  = useRef(null);
  const rightPanelRef = useRef(null);
  const [leftCollapsed,  setLeftCollapsed]  = useState(false);
  const [rightCollapsed, setRightCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState(tabFromURL);

  const toggleLeft = useCallback(() => {
    if (leftCollapsed)  leftPanelRef.current?.expand();
    else                leftPanelRef.current?.collapse();
  }, [leftCollapsed]);

  const toggleRight = useCallback(() => {
    if (rightCollapsed) rightPanelRef.current?.expand();
    else                rightPanelRef.current?.collapse();
  }, [rightCollapsed]);

  useEffect(() => {
    const handler = () => setActiveTab(tabFromURL());
    window.addEventListener('popstate', handler);
    return () => window.removeEventListener('popstate', handler);
  }, []);

  return (
    <div className="d-flex flex-column" style={{ height: '100vh', background: 'var(--eh-navy)' }}>
      <nav className="navbar navbar-dark bg-primary px-4 py-2 flex-shrink-0">
        <span
          className="navbar-brand mb-0 d-flex align-items-center gap-3"
          style={{ cursor: 'pointer' }}
          onClick={() => onNavigate('home')}
        >
          <img src="/enlace-logo-white.svg" alt="Enlace Health" style={{ height: 36 }} />
          <span style={{ fontWeight: 300, opacity: 0.65, fontSize: '0.8rem', letterSpacing: '0.06em', textTransform: 'uppercase', color: '#fff' }}>
            Enlace Intelligence
          </span>
        </span>
        {currentUser && (
          <AccountDropdown currentUser={currentUser} onLogout={onLogout} />
        )}
      </nav>

      <div className="dash-tabs-bar flex-shrink-0">
        {TABS.map(t => (
          <button
            key={t.key}
            className={`dash-tab-btn${activeTab === t.key ? ' active' : ''}`}
            onClick={() => {
              history.pushState(null, '', '/dashboard?tab=' + t.key);
              setActiveTab(t.key);
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="flex-grow-1 overflow-hidden">
        <PanelGroup direction="horizontal" style={{ height: '100%', background: 'var(--eh-bg-page)' }}>
          <Panel
            ref={leftPanelRef}
            defaultSize={75}
            minSize={0}
            collapsible
            collapsedSize={0}
            onCollapse={() => setLeftCollapsed(true)}
            onExpand={()   => setLeftCollapsed(false)}
            style={{ background: 'var(--eh-bg-page)' }}
          >
            <FilterProvider>
              <TabContent activeTab={activeTab} />
            </FilterProvider>
          </Panel>

          <PanelResizeHandle className="resize-handle">
            <div className="resize-handle-inner">
              <button
                className="collapse-btn"
                title={leftCollapsed ? 'Show dashboards' : 'Hide dashboards'}
                onClick={toggleLeft}
                onPointerDown={e => e.stopPropagation()}
              >
                {leftCollapsed ? '▶' : '◀'}
              </button>
              <div className="resize-handle-bar" />
              <button
                className="collapse-btn"
                title={rightCollapsed ? 'Show chat' : 'Hide chat'}
                onClick={toggleRight}
                onPointerDown={e => e.stopPropagation()}
              >
                {rightCollapsed ? '◀' : '▶'}
              </button>
            </div>
          </PanelResizeHandle>

          <Panel
            ref={rightPanelRef}
            defaultSize={25}
            minSize={0}
            collapsible
            collapsedSize={0}
            onCollapse={() => setRightCollapsed(true)}
            onExpand={()   => setRightCollapsed(false)}
            style={{ background: 'var(--eh-bg-page)' }}
          >
            <ChatPanel activeTab={activeTab} chatStateRef={chatStateRef} />
          </Panel>
        </PanelGroup>
      </div>
    </div>
  );
}
